import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight,
  Download,
  Github,
  Linkedin,
  Mail,
  ExternalLink,
  Star,
  Award,
  Calendar,
} from "lucide-react";
import { profileData, skills } from "../data/mock";
import { motion, useScroll, useTransform } from "framer-motion";
import TestimonialSlider from "../components/TestimonialSlider";
import { useData } from "@/contexts/DataContext";
import DomPurifyContent from "@/utils/domPurifyContent";
import CertificatePdfThumbnail from "@/components/ui/CertificatePdfThumbnail";
import {
  getCertificateFileUrl,
  isPdfFile,
} from "@/utils/certificateFile";

const Home = () => {
  const { blogs, projects, certifications } = useData();
  const featuredProjects = projects
    ? projects?.filter((project) => project?.featured).slice(0, 3)
    : [];
  const featuredBlogs = blogs
    ? blogs?.filter((blog) => blog.featured).slice(0, 2)
    : [];
  const topCertifications = [...(certifications || [])]
    .sort((a, b) => new Date(b.issueDate) - new Date(a.issueDate))
    .slice(0, 3);
  // console.log(featuredBlogs)
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  // if(!featuredProjects || featuredProjects.length === 0){
  //   return <>
  //     <p>No featured Project</p>
  //   </>
  // }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-black/5 dark:from-purple-950/20 dark:via-background dark:to-black/40 overflow-hidden mt-0 sm:pt-12 md:pt-12 xs:pt-12 xxss:pt-12">
        <motion.div
          style={{ y }}
          className="absolute inset-0 bg-grid-pattern opacity-5"
        />
        <div className="container mx-auto px-4 relative z-10 xs:px-5 xxss:px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-sm font-medium"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Available for new opportunities
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="text-4xl lg:text-6xl md:text-xl sm:text-lg font-bold text-gray-900 dark:text-white leading-tight xxss:w-[100%]"
                >
                  Hi, I'm{" "}
                  <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
                    {profileData.name.split(" ").slice(0, 2).join(" ")}
                  </span>
                </motion.h1>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="text-xl lg:text-2xl md:text-[12px] sm:text-[12px] text-gray-600 dark:text-gray-300 font-medium"
                >
                  {profileData.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="text-lg text-gray-600 dark:text-gray-400 max-w-xl leading-relaxed breaks-words xs:w-[100%]
                  xxss:w-[100%]"
                >
                  {profileData.bio}
                </motion.p>
              </div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    asChild
                    size="lg"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Link to="/portfolio">
                      View Portfolio
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/contact">
                      Get In Touch
                      <Mail className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </motion.div>
              </motion.div>

              {/* Social Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="flex space-x-4"
              >
                {[
                  { icon: Github, url: profileData.social.github },
                  { icon: Linkedin, url: profileData.social.linkedin },
                  { icon: Mail, url: `mailto:${profileData.email}` },
                ].map((social, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      asChild
                      className="hover:text-purple-600"
                    >
                      <a
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <social.icon className="h-5 w-5" />
                      </a>
                    </Button>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Content - Profile Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                <motion.div
                  whileHover={{ scale: 1.02, rotate: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="w-80 h-80 rounded-2xl overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/20 dark:to-purple-800/20 profile-img"
                >
                  <img
                    src={profileData.profileImage}
                    alt={profileData.name}
                    className="w-full h-full object-cover profile-img"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.4, type: "spring", stiffness: 200 }}
                  className="absolute -bottom-4 -right-4 w-24 h-24 bg-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
                >
                  5+ Years
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skills/Expertise Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Areas of Expertise
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Specialized in multiple domains with hands-on experience in modern
              technologies and frameworks.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {skills.map((skillGroup, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="p-6 hover:shadow-lg transition-shadow border-l-4 border-l-purple-600 h-full">
                  <CardContent className="p-0">
                    <motion.h3
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="text-xl font-semibold mb-4 text-purple-700 dark:text-purple-300"
                    >
                      {skillGroup.category}
                    </motion.h3>
                    <div className="flex flex-wrap gap-2">
                      {skillGroup.technologies.map((tech, techIndex) => (
                        <motion.div
                          key={techIndex}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 * techIndex }}
                          whileHover={{ scale: 1.1 }}
                        >
                          <Badge
                            variant="secondary"
                            className="text-xs cursor-default"
                          >
                            {tech}
                          </Badge>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-center mb-12"
          >
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured Projects
              </h2>
              <p className="text-lg text-muted-foreground">
                Showcase of my recent work and technical achievements.
              </p>
            </div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button variant="outline" asChild>
                <Link to="/portfolio">
                  View All Projects
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project?._id}
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="aspect-video overflow-hidden">
                    <motion.img
                      src={project.images[0]?.url}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      whileHover={{ scale: 1.1 }}
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div>
                        <Badge variant="outline" className="text-xs mb-2">
                          {project.category}
                        </Badge>
                        <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-600 transition-colors">
                          {project.title}
                        </h3>
                        <DomPurifyContent
                          className="text-muted-foreground text-sm"
                          content={
                            project.description.length > 20
                              ? project.description.substring(0, 90) + "..."
                              : project.description
                          }
                        />
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {project.technologies
                          .slice(0, 3)
                          .map((tech, techIndex) => (
                            <motion.div
                              key={techIndex}
                              whileHover={{ scale: 1.1 }}
                            >
                              <Badge variant="secondary" className="text-xs">
                                {tech}
                              </Badge>
                            </motion.div>
                          ))}
                        {project.technologies.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{project.technologies.length - 3} more
                          </Badge>
                        )}
                      </div>

                      <div className="flex justify-between items-center pt-4">
                        <div className="flex space-x-2">
                          {project.liveUrl && (
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button size="sm" variant="ghost" asChild>
                                <a
                                  href={project.liveUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4 mr-1" />
                                  Live
                                </a>
                              </Button>
                            </motion.div>
                          )}
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Button size="sm" variant="ghost" asChild>
                              <a
                                href={project.githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Github className="h-4 w-4 mr-1" />
                                Code
                              </a>
                            </Button>
                          </motion.div>
                        </div>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button size="sm" asChild>
                            <Link to={`/portfolio/${project?._id}`}>
                              Details
                            </Link>
                          </Button>
                        </motion.div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      {topCertifications.length > 0 && (
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-between items-center mb-12 gap-4 flex-wrap"
            >
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                  Certifications
                </h2>
                <p className="text-lg text-muted-foreground">
                  Recent credentials that back my skills and expertise.
                </p>
              </div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" asChild>
                  <Link to="/certifications">
                    View All Certifications
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {topCertifications.map((cert) => (
                <motion.div
                  key={cert._id}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden h-full flex flex-col">
                    <Link
                      to={`/certifications/${cert._id}`}
                      className="aspect-video overflow-hidden bg-muted block"
                    >
                      {isPdfFile(cert.file) ? (
                        <CertificatePdfThumbnail file={cert.file} />
                      ) : (
                        <motion.img
                          src={getCertificateFileUrl(cert.file)}
                          alt={cert.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          whileHover={{ scale: 1.1 }}
                        />
                      )}
                    </Link>
                    <CardContent className="p-6 flex flex-col flex-1 gap-4">
                      <div className="space-y-3 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {cert.industry}
                          </Badge>
                          {cert.company && (
                            <Badge variant="secondary" className="text-xs">
                              {cert.company}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {cert.issueDate
                              ? new Date(cert.issueDate).toLocaleDateString(
                                  undefined,
                                  {
                                    year: "numeric",
                                    month: "short",
                                  }
                                )
                              : "—"}
                          </span>
                        </div>
                        <Link to={`/certifications/${cert._id}`}>
                          <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-600 transition-colors flex items-start gap-2">
                            <Award className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                            <span className="line-clamp-2">{cert.title}</span>
                          </h3>
                        </Link>
                        {cert.description && (
                          <DomPurifyContent
                            className="text-muted-foreground text-sm line-clamp-2"
                            content={cert.description}
                          />
                        )}
                      </div>
                      <div className="flex justify-between items-center pt-2 gap-2">
                        {cert.verifyUrl?.trim() ? (
                          <Button size="sm" variant="ghost" asChild>
                            <a
                              href={cert.verifyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Verify
                            </a>
                          </Button>
                        ) : (
                          <span />
                        )}
                        <Button size="sm" asChild>
                          <Link to={`/certifications/${cert._id}`}>
                            Details
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              What Clients Say
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Feedback from clients around the world who have trusted me with
              their projects.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <TestimonialSlider className="max-w-5xl mx-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="text-center mt-12"
          >
            <Button variant="outline" size="lg" asChild>
              <Link to="/testimonials">
                View All Testimonials
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Featured Blogs Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Latest from Blog
              </h2>
              <p className="text-lg text-muted-foreground">
                Insights and tutorials on web development and data engineering.
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/blog">
                View All Posts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {featuredBlogs.map((blog) => (
              <Card
                key={blog?._id}
                className="group hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={blog.images[0].url}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center text-sm text-muted-foreground mb-2">
                        <span>
                          {new Date(blog.createdAt).toLocaleDateString()}
                        </span>
                        <span className="mx-2">•</span>
                        <span>{blog.readTime}</span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-600 transition-colors">
                        {blog.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {blog.excerpt}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex justify-between items-center pt-4">
                      <Button size="sm" variant="ghost" asChild>
                        <a
                          href={blog.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Read on Medium
                          <ExternalLink className="h-4 w-4 ml-1" />
                        </a>
                      </Button>
                      <Button size="sm" asChild>
                        <Link to={`/blog/${blog?._id}`}>Read More</Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-black text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Ready to bring your ideas to life?
            </h2>
            <p className="text-xl text-purple-100">
              Let's collaborate and build something amazing together. I'm always
              excited to work on new challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link
                  to="/contact"
                  className="text-purple-600 hover:text-purple-700"
                >
                  Start a Project
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-purple-600"
                asChild
              >
                <a
                  href={`https://drive.google.com/file/d/1njo7UH7KvynYqkkzoQ4KZ3WAhka6h_LN/view?usp=sharing`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Resume
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
